import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo, useCallback, useState } from 'react';
// hooks
import { useLocalStorage } from 'src/hooks/use-local-storage';
// utils
import { localStorageGetItem } from 'src/utils/storage-available';
// contexts
import { useThemeSettings } from 'src/contexts/ThemeSettingsContext';
//
import { SettingsContext } from './settings-context';

// ----------------------------------------------------------------------

const STORAGE_KEY = 'settings';

// Map local settings field names to database field names
const FIELD_MAPPING = {
  themeMode: 'mode',
  themeContrast: 'contrast',
  themeDirection: 'direction',
  themeLayout: 'layout',
  themeStretch: 'stretch',
  themeColorPresets: 'colorPreset'
};

export function SettingsProvider({ children, defaultSettings }) {
  const { state, update, reset } = useLocalStorage(STORAGE_KEY, defaultSettings);
  const themeSettings = useThemeSettings();

  const [openDrawer, setOpenDrawer] = useState(false);

  const isArabic = localStorageGetItem('i18nextLng') === 'ar';

  // Sync database settings to local settings on initial load
  useEffect(() => {
    if (themeSettings?.settings && !themeSettings.loading) {
      const dbSettings = themeSettings.settings;

      // Map database fields to local settings fields
      const updates = {};
      let hasChanges = false;

      Object.entries(FIELD_MAPPING).forEach(([localField, dbField]) => {
        if (dbSettings[dbField] !== undefined && state[localField] !== dbSettings[dbField]) {
          // Special handling for field name differences
          if (localField === 'themeContrast') {
            // Database uses 'normal'/'high', local uses 'default'/'bold'
            updates[localField] = dbSettings[dbField] === 'normal' ? 'default' : 'bold';
          } else if (localField === 'themeLayout') {
            // Database uses 'compact', local uses 'mini'
            updates[localField] = dbSettings[dbField] === 'compact' ? 'mini' : dbSettings[dbField];
          } else {
            updates[localField] = dbSettings[dbField];
          }
          hasChanges = true;
        }
      });

      // Apply updates if there are changes
      if (hasChanges) {
        Object.entries(updates).forEach(([field, value]) => {
          update(field, value);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeSettings?.settings, themeSettings?.loading]);

  useEffect(() => {
    if (isArabic) {
      onChangeDirectionByLang('ar');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArabic]);

  // Enhanced update function that syncs to database
  const onUpdate = useCallback(
    (field, value) => {
      // Update local storage
      update(field, value);

      // Sync to database if ThemeSettings is available
      if (themeSettings && !themeSettings.loading) {
        const dbField = FIELD_MAPPING[field];

        if (dbField) {
          let dbValue = value;

          // Handle field value transformations
          if (field === 'themeContrast') {
            // Local 'default'/'bold' -> Database 'normal'/'high'
            dbValue = value === 'default' ? 'normal' : 'high';
          } else if (field === 'themeLayout') {
            // Local 'mini' -> Database 'compact'
            dbValue = value === 'mini' ? 'compact' : value;
          }

          // Update database
          themeSettings.updateSettings({ [dbField]: dbValue });
        }
      }
    },
    [update, themeSettings]
  );

  // Enhanced reset function that syncs to database
  const onReset = useCallback(() => {
    // Reset local storage
    reset();

    // Reset database settings
    if (themeSettings && !themeSettings.loading) {
      themeSettings.resetSettings();
    }
  }, [reset, themeSettings]);

  // Direction by lang
  const onChangeDirectionByLang = useCallback(
    (lang) => {
      const direction = lang === 'ar' ? 'rtl' : 'ltr';
      onUpdate('themeDirection', direction);
    },
    [onUpdate]
  );

  // Drawer
  const onToggleDrawer = useCallback(() => {
    setOpenDrawer((prev) => !prev);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  const canReset = !isEqual(state, defaultSettings);

  const memoizedValue = useMemo(
    () => ({
      ...state,
      onUpdate,
      // Direction
      onChangeDirectionByLang,
      // Reset
      canReset,
      onReset,
      // Drawer
      open: openDrawer,
      onToggle: onToggleDrawer,
      onClose: onCloseDrawer,
      // Database sync status
      syncing: themeSettings?.syncing || false,
    }),
    [
      onReset,
      onUpdate,
      state,
      canReset,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
      onChangeDirectionByLang,
      themeSettings?.syncing,
    ]
  );

  return <SettingsContext.Provider value={memoizedValue}>{children}</SettingsContext.Provider>;
}

SettingsProvider.propTypes = {
  children: PropTypes.node,
  defaultSettings: PropTypes.object,
};
